import { Table, Tag, Typography } from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { FecJournalMappingStatus } from '../../../../enums/fecJournalMappingStatus'
import { FecJournalType, isFecJournalTypeWithLinkedAccount } from '../../../../enums/fecJournalType'
import { useUpdateFecJournalMapping } from '../../../../hooks/useUpdateFecJournalMapping'
import type { FecJournalMappingWithImport } from '../../../../types/fecJournalMappingWithImport'
import { getFecJournalMappingStatus } from '../../../../utils/getFecJournalMappingStatus'
import { FecImportMappingTableScrollArea } from '../FecImportMappingTableScrollArea'
import { JournalStatusBadge } from './elements/JournalStatusBadge'
import { JournalTypeSelect } from './elements/JournalTypeSelect'
import { LinkedAccountSelect } from './elements/LinkedAccountSelect'

interface FecImportJournalsTableProps {
  isLoading: boolean
  journalMappings: FecJournalMappingWithImport[]
  /** Rows that stand for an import whose journals are confirmed already: the backend takes no more changes there. */
  lockedMappingIds: ReadonlySet<string>
}

export const FecImportJournalsTable = ({
  isLoading,
  journalMappings,
  lockedMappingIds,
}: FecImportJournalsTableProps): ReactElement => {
  const { t } = useTranslation()
  const { pendingMappingId, update } = useUpdateFecJournalMapping()

  const handleTypeSelect = useCallback(
    (mapping: FecJournalMappingWithImport, type: FecJournalType) => {
      update({
        fecImportId: mapping.fecImportId,
        mappingId: mapping.id,
        payload: { detectedType: type },
      })
    },
    [update],
  )

  const handleLinkedAccountChange = useCallback(
    (mapping: FecJournalMappingWithImport, accountId?: string) => {
      update({
        fecImportId: mapping.fecImportId,
        mappingId: mapping.id,
        payload: { linkedBankAccountId: accountId ?? null },
      })
    },
    [update],
  )

  return (
    <FecImportMappingTableScrollArea>
      <Table compact>
        <Table.Content>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={80}>
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.journals.columns.code')}
              </Table.HeaderCell>
              <Table.HeaderCell width={220}>
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.journals.columns.label')}
              </Table.HeaderCell>
              <Table.HeaderCell width={200}>
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.journals.columns.type')}
              </Table.HeaderCell>
              <Table.HeaderCell width={220}>
                {t(
                  'settings.organization.accounting.fec_file_import.modal.mapping_step.journals.columns.linked_account',
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={160}>
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.journals.columns.status')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          {isLoading ? (
            <Table.Skeleton columns={5} />
          ) : (
            <Table.Body>
              {journalMappings.map((mapping) => {
                const status = getFecJournalMappingStatus(mapping.journalCode, mapping.journalLib)
                const isCodeAccent = status === FecJournalMappingStatus.CustomJournal
                const isLabelAccent = status !== FecJournalMappingStatus.Standard
                const isPending = pendingMappingId === mapping.id
                const isEditDisabled = isPending || lockedMappingIds.has(mapping.id)

                return (
                  <Table.Row key={mapping.id}>
                    <Table.Cell>
                      <Tag
                        color={isCodeAccent ? 'blue' : 'neutral'}
                        label={mapping.journalCode}
                        size="small"
                        type="subtle"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Typography.Text color={isLabelAccent ? colors.content.accent.bold : undefined}>
                        {mapping.journalLib}
                      </Typography.Text>
                    </Table.Cell>
                    <Table.Cell>
                      <JournalTypeSelect
                        disabled={isEditDisabled}
                        onSelect={(type) => handleTypeSelect(mapping, type)}
                        selectedType={mapping.detectedType}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      {isFecJournalTypeWithLinkedAccount(mapping.detectedType) ? (
                        <LinkedAccountSelect
                          disabled={isEditDisabled}
                          onSelect={(accountId) => handleLinkedAccountChange(mapping, accountId)}
                          selectedAccountId={mapping.linkedBankAccountId ?? undefined}
                        />
                      ) : null}
                    </Table.Cell>
                    <Table.Cell>
                      <JournalStatusBadge status={status} />
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          )}
        </Table.Content>
      </Table>
    </FecImportMappingTableScrollArea>
  )
}
